import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, BrutalCard, Chip, EmptyState } from "../components/brutal";
import { Briefcase, MapPin, ArrowRight, Search, Share2, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import JobDetailModal from "../components/JobDetailModal";

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [activeJob, setActiveJob] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const locations = Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))).sort();

  const filtered = jobs.filter((j) => {
    if (q && !j.title.toLowerCase().includes(q.toLowerCase())) return false;
    if (loc && j.location !== loc) return false;
    return true;
  });

  return (
    <div>
      <PageHeader
        title="Open Roles"
        subtitle="Know someone great? Refer them or share a public link — points either way."
      />

      <div className="brutal p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 border-2 border-black rounded-xl px-3 py-2 bg-white">
          <Search className="w-4 h-4" />
          <input
            placeholder="Search roles…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 outline-none bg-transparent"
            data-testid="jobs-search"
          />
        </div>
        <select
          value={loc}
          onChange={(e) => setLoc(e.target.value)}
          className="border-2 border-black rounded-xl px-3 py-2 font-semibold bg-white"
          data-testid="jobs-location-filter"
        >
          <option value="">All locations</option>
          {locations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No jobs match your filters" description="Try clearing your filters." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((j) => (
            <BrutalCard
              key={j.id}
              className="p-6 flex flex-col cursor-pointer hover:translate-y-[-2px] transition-transform"
              data-testid={`job-card-${j.id}`}
              onClick={() => setActiveJob(j)}
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 border-2 border-black rounded-xl overflow-hidden flex items-center justify-center bg-brand-blue text-white shadow-brutal">
                  {j.logo_url ? (
                    <img src={j.logo_url} alt={j.title} className="w-full h-full object-cover" />
                  ) : (
                    <Briefcase className="w-5 h-5" strokeWidth={2.5} />
                  )}
                </div>
                {j.status === "open" ? <Chip color="bg-brand-green">OPEN</Chip> : <Chip color="bg-neutral-300">CLOSED</Chip>}
              </div>
              <h3 className="font-display font-black text-2xl mt-4 leading-tight">{j.title}</h3>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                {j.location && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700">
                    <MapPin className="w-3 h-3" /> {j.location}
                  </span>
                )}
              </div>
              <p className="text-sm text-neutral-700 mt-3 line-clamp-3 flex-1">{j.description}</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveJob(j); }}
                  className="btn-brutal btn-white text-xs"
                  data-testid={`view-job-${j.id}`}
                >
                  <Eye className="w-4 h-4" /> View & share
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nav(`/app/refer?jobId=${j.id}`); }}
                  className="btn-brutal btn-primary text-xs"
                  data-testid={`refer-job-${j.id}`}
                  disabled={j.status !== "open"}
                >
                  Refer <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              {j.status === "open" && (
                <div className="text-[10px] text-neutral-500 mt-2 flex items-center gap-1 justify-center">
                  <Share2 className="w-3 h-3" /> Tap to share with friends
                </div>
              )}
            </BrutalCard>
          ))}
        </div>
      )}

      <JobDetailModal job={activeJob} open={!!activeJob} onClose={() => setActiveJob(null)} />
    </div>
  );
}
